import PizZip from 'pizzip';

function escapeXml(value) {
  return String(value ?? '').replace(/[<>&"']/g, (character) => ({
    '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;',
  })[character]);
}

function styleId(xml, address) {
  const match = xml.match(new RegExp(`<x:c r="${address}" s="(\\d+)"`));
  return match?.[1] || '0';
}

function textCell(address, style, value) {
  return `<x:c r="${address}" s="${style}" t="inlineStr"><x:is><x:t xml:space="preserve">${escapeXml(value)}</x:t></x:is></x:c>`;
}

function numberCell(address, style, value, formula = '') {
  return `<x:c r="${address}" s="${style}">${formula ? `<x:f>${escapeXml(formula)}</x:f>` : ''}<x:v>${Number(value) || 0}</x:v></x:c>`;
}

function excelDateSerial(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const [, year, month, day] = match.map(Number);
  return (Date.UTC(year, month - 1, day) - Date.UTC(1899, 11, 30)) / 86400000;
}

function exportDateLabel() {
  return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date());
}

export function populateOrdersTemplate(templateBytes, orders) {
  const zip = new PizZip(templateBytes);
  const sheetPath = 'xl/worksheets/sheet1.xml';
  const originalXml = zip.file(sheetPath)?.asText();
  if (!originalXml) throw new Error('Orders worksheet is missing from the Excel template');

  const sheetDataMatch = originalXml.match(/<x:sheetData>([\s\S]*?)<\/x:sheetData>/);
  if (!sheetDataMatch) throw new Error('Orders worksheet data is invalid');
  const fixedRows = [...sheetDataMatch[1].matchAll(/<x:row r="(\d+)"[\s\S]*?<\/x:row>/g)]
    .filter((match) => Number(match[1]) <= 6)
    .map((match) => match[0]);

  const styles = {
    metaDate: styleId(originalXml, 'A5'), metaCount: styleId(originalXml, 'D5'),
    internal: styleId(originalXml, 'A7'), text: styleId(originalXml, 'B7'),
    date: styleId(originalXml, 'D7'), currency: styleId(originalXml, 'F7'),
    totalLabel: styleId(originalXml, 'A8'), totalValue: styleId(originalXml, 'F8'),
  };

  fixedRows[3] = `<x:row r="5">${textCell('A5', styles.metaDate, exportDateLabel())}<x:c r="B5" s="${styleId(originalXml, 'B5')}"/><x:c r="C5" s="${styleId(originalXml, 'C5')}"/>${numberCell('D5', styles.metaCount, orders.length)}<x:c r="E5" s="${styleId(originalXml, 'E5')}"/><x:c r="F5" s="${styleId(originalXml, 'F5')}"/></x:row>`;

  let visibleTotal = 0;
  const dataRows = orders.map((order, index) => {
    const rowNumber = 7 + index;
    const total = Number(order.price) || 0;
    const dateSerial = excelDateSerial(order.creation_date);
    visibleTotal += total;
    return `<x:row r="${rowNumber}" ht="22" customHeight="1">${numberCell(`A${rowNumber}`, styles.internal, order.order_number)}${textCell(`B${rowNumber}`, styles.text, order.orderid)}${textCell(`C${rowNumber}`, styles.text, order.owner)}${dateSerial == null ? textCell(`D${rowNumber}`, styles.text, order.creation_date || '') : numberCell(`D${rowNumber}`, styles.date, dateSerial)}${textCell(`E${rowNumber}`, styles.text, order.status)}${numberCell(`F${rowNumber}`, styles.currency, total)}</x:row>`;
  });
  const totalRow = 7 + orders.length;
  const totalCells = [textCell(`A${totalRow}`, styles.totalLabel, 'TOTAL VISIBLE ORDERS')];
  for (const column of ['B', 'C', 'D', 'E']) totalCells.push(`<x:c r="${column}${totalRow}" s="${styles.totalLabel}"/>`);
  totalCells.push(numberCell(`F${totalRow}`, styles.totalValue, visibleTotal, `SUM(F7:F${6 + orders.length})`));
  const rowsXml = [...fixedRows, ...dataRows, `<x:row r="${totalRow}" ht="24" customHeight="1">${totalCells.join('')}</x:row>`].join('');

  let xml = originalXml.replace(/<x:sheetData>[\s\S]*?<\/x:sheetData>/, `<x:sheetData>${rowsXml}</x:sheetData>`);
  xml = xml.replace(/<x:mergeCell ref="A8:E8"\s*\/>/, `<x:mergeCell ref="A${totalRow}:E${totalRow}" />`);
  xml = xml.replace(/<x:autoFilter[^>]*\/>/g, '');
  xml = xml.replace('</x:sheetData>', `</x:sheetData><x:autoFilter ref="A6:F${6 + orders.length}" />`);
  xml = xml.replace(/<x:sheetView([^>]*)\/>/, '<x:sheetView$1><x:pane ySplit="6" topLeftCell="A7" activePane="bottomLeft" state="frozen" /></x:sheetView>');
  zip.file(sheetPath, xml);

  return zip.generate({
    type: 'arraybuffer',
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    compression: 'DEFLATE',
  });
}
